/** ***********
*
* <asset.reducer.ts v1.0>
*
* Copyright (c) 2022 AIRBUS OPERATIONS
*
* Contributors:
*  NG6C740 [thomas.pereira.external@airbus.com]
*
* All rights reserved.
**************/

import { Action, createReducer, on } from '@ngrx/store';
import { isEqual } from 'lodash';
import { Asset } from 'src/app/models/asset';
import { Zone } from 'src/app/models/zone';
import {
    mappingSomeAssetsFamilies,
    successDeleteAssets,
    __successDissociateAssetTracker,
    __successAssociateAssetTracker,
    mappingAssetsWithTrackersData,
    mappingAllAssets,
    successUploadAssetPicture,
    fetchAllAssets,
} from '../actions/asset.actions';

export interface State {
    data: {
        assets: { [id: string]: Asset },
        assetsUpdated?: Asset[]
    };
    loaded: boolean;
    ready: boolean;
}

// default state
const initialState: State = {
    data: {
        assets: {},
    },
    loaded: false,
    ready: false
};

const assetReducer = createReducer(
    initialState,
    on(fetchAllAssets, (state) => ({
        ...state,
        loaded: false,
        ready: false,
    })),
    on(mappingAllAssets, (state, action) => {
        const zoneMapping: Record<Zone['id'], Zone['name']> = mapZones(action.zones);
        return {
            ...state,
            loaded: true,
            data: (() => {
                const datas = {
                    assets: {}
                };
                for (const asset of action.assets) {
                    asset.family = action.families[asset.familyId];
                    // trackers - case of refetch all assets
                    const tracker = state.data.assets?.[asset.id]?.tracker;
                    if (tracker) {
                        asset.tracker = state.data.assets[asset.id].tracker;
                    }
                    asset.associated = asset.trackerId ? true : false;
                    asset.sectionMsn = asset.msn ? [{ msn: asset.msn, section: asset.section }]
                        : asset.loadedElements?.map(elem => ({ msn: elem.msn, section: elem.section }))
                        || [{ msn: null, section: null }];
                    asset.last_zone = asset.last_zone?.zone1?.id ? asset.last_zone : computeLastZone(zoneMapping, asset);
                    // harmonize state
                    if (asset.last_event?.state === 'ON_THE_MOVE') {
                        asset.last_event.state = 'OTM';
                    }
                    datas.assets[asset.id] = asset;
                }
                return datas;
            })()
        };
    }),

    on(mappingSomeAssetsFamilies, (state, action) => {
        const zoneMapping: Record<Zone['id'], Zone['name']> = mapZones(action.zones);

        return {
            ...state,
            loaded: true,
            data: (() => {
                const datas = { ...state.data };
                for (const asset of action.assets) {
                    asset.family = action.families[asset.familyId];
                    if (asset.trackerId) {
                        asset.associated = true;
                        asset.tracker = action.trackers[asset.trackerId];
                    }
                    asset.last_zone = computeLastZone(zoneMapping, asset);
                    // harmonize state
                    if (asset.last_event?.state === 'ON_THE_MOVE') {
                        asset.last_event.state = 'OTM';
                    }
                    datas.assets[asset.id] = asset;
                }
                return datas;
            })()
        };
    }),

    on(mappingAssetsWithTrackersData, (state, action) => {
        return {
            ...state,
            loaded: true,
            ready: true,
            data: (() => {
                const data = { ...state.data };
                const assets = { ...state.data.assets };
                const trackers = action.trackers;
                for (const tracker of trackers) {
                    if (tracker.assetId && assets[tracker.assetId]) {
                        const asset = assets[tracker.assetId];
                        asset.additionalData = {
                            ...asset.additionalData,
                            ...tracker.additionalData
                        };
                        asset.tracker = { ...tracker };

                        // delete additional data if empty
                        if (isEqual(asset.additionalData, {})) {
                            delete asset.additionalData;
                        }


                    }
                }
                return data;
            })()
        };
    }),

    on(successDeleteAssets, (state, action) => {
        return {
            ...state,
            data: (() => {
                const data = { ...state.data };
                for (const asset of action.payload) {
                    delete data.assets[asset.id];
                }
                return data;
            })()
        };
    }),

    on(__successAssociateAssetTracker, (state, action) => {
        return {
            ...state,
            data: (() => {
                const data = { ...state.data };
                const asset = data.assets[action.assetId];
                asset.associated = true;
                asset.tracker = action.tracker;
                return data;
            })()
        };
    }),

    on(__successDissociateAssetTracker, (state, action) => {
        return {
            ...state,
            data: (() => {
                const data = { ...state.data };
                const asset = data.assets[action.assetId];
                asset.associated = false;
                delete asset.tracker;
                return data;
            })()
        };
    }),

    on(successUploadAssetPicture, (state, action) => {
        return {
            ...state,
            data: (() => {
                const data = { ...state.data };
                const asset = data.assets[action.assetId];
                asset.picture = action.picture;
                return data;
            })()
        };
    })


);

export function reducer(state: State | undefined, action: Action) {
    return assetReducer(state, action);
}

const computeLastZone = (zoneMapping: Record<Zone['id'], Zone['name']>, asset: Asset) => {
    const lastZone: typeof asset.last_zone = { zone1: null, zone2: null, zone3: null };
    let zoneId: string;

    if (asset.last_zone?.zone1) {
        // Force it to the type string as it is what the service sends
        zoneId = asset.last_zone?.zone1 as unknown as string;
        lastZone.zone1 = {
            id: zoneId,
            name: zoneMapping[zoneId],
        };
    }
    if (asset.last_zone?.zone2) {
        // Force it to the type string as it is what the service sends
        zoneId = asset.last_zone?.zone2 as unknown as string;
        lastZone.zone2 = {
            id: zoneId,
            name: zoneMapping[zoneId],
        };
    }
    if (asset.last_zone?.zone3) {
        // Force it to the type string as it is what the service sends
        zoneId = asset.last_zone?.zone3 as unknown as string;
        lastZone.zone3 = {
            id: zoneId,
            name: zoneMapping[zoneId],
        };
    }
    return lastZone;
};

function mapZones(zones: Zone[]): Record<string, string> {
    return zones.reduce(
        (mapping, zone) => {
            mapping[zone.id] = zone.name;
            return mapping;
        }, {}
    );
}
